import React from "react";
import {
  Shield,
  Clock,
  Award,
  Users,
  Leaf,
  CheckCircle,
  AlertCircle,
  HeartHandshake,
} from "lucide-react";
import { motion } from "framer-motion";

const WhyChooseUs = () => {
  const mainFeatures = [
    {
      icon: Leaf,
      title: "Eco Friendly",
      description:
        "We use environmentally friendly cleaning products that are free from harmful chemicals and toxins, ensuring safety for both our staff and the environment.",
      color: "from-green-500 to-green-600",
    },
    {
      icon: CheckCircle,
      title: "High Quality",
      description:
        "Our experienced team delivers exceptional quality cleaning solutions with meticulous attention to detail, exceeding client expectations.",
      color: "from-primary to-primary-dark",
    },
    {
      icon: AlertCircle,
      title: "Safety First",
      description:
        "We prioritize safety with strict protocols and comprehensive staff training to protect our employees, clients, and environment.",
      color: "from-secondary to-secondary-dark",
    },
    {
      icon: HeartHandshake,
      title: "24-Hour Guarantee",
      description:
        "Unsatisfied with any area we clean? We guarantee a re-clean within 24 hours, ensuring your complete satisfaction.",
      color: "from-blue-500 to-blue-600",
    },
  ];

  const reasons = [
    {
      icon: Shield,
      title: "Fully Insured",
      description:
        "Complete coverage for peace of mind, protecting your property during all cleaning services.",
    },
    {
      icon: Clock,
      title: "24/7 Availability",
      description:
        "Round-the-clock service with flexible scheduling including after-hours and weekends.",
    },
    {
      icon: Award,
      title: "Quality Guaranteed",
      description:
        "Our satisfaction guarantee ensures the highest quality cleaning service every time.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description:
        "Professional, vetted cleaning staff dedicated to exceptional results.",
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-secondary/10 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h2 className="text-4xl font-bold sm:text-5xl">
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Why Choose Envipro Solutions?
            </span>
          </h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            We combine professional expertise with exceptional service to
            deliver outstanding results for our clients.
          </p>
        </motion.div>

        {/* Main Features */}
        <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {mainFeatures.map((feature, index) => (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              key={feature.title}
              className="relative group"
            >
              <div className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl transform group-hover:scale-105 transition-transform duration-300" />
                <div className="relative">
                  <div
                    className={`w-16 h-16 mx-auto flex items-center justify-center rounded-xl bg-gradient-to-r ${feature.color} transform rotate-3 group-hover:rotate-6 transition-transform duration-300`}
                  >
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-gray-900 text-center">
                    {feature.title}
                  </h3>
                  <p className="mt-4 text-gray-600 text-center">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon;
            return (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                key={reason.title}
                className="relative group flex flex-col items-center text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-primary to-secondary group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  {reason.title}
                </h3>
                <p className="mt-2 text-gray-600">{reason.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row gap-6 items-center justify-center bg-white px-8 py-4 rounded-full shadow-md">
            <div className="flex items-center space-x-2">
              <Shield className="h-6 w-6 text-primary" />
              <span className="text-gray-700">Fully Insured</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
            <div className="flex items-center space-x-2">
              <Award className="h-6 w-6 text-primary" />
              <span className="text-gray-700">Certified Professionals</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
            <div className="flex items-center space-x-2">
              <Users className="h-6 w-6 text-primary" />
              <span className="text-gray-700">500+ Happy Clients</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
